import { useEffect, useMemo, useState } from "react";
import type { Trade } from "../../sdk";
import { Link } from "../../components/Link";
import { Avatar } from "../../components/game";
import { useOwner } from "../../state/account";
import { useRecentTrades } from "../../state/market";
import { fmt, fromRaw, toRaw } from "../../lib/num";
import { shortAddress } from "../../wallet/address";

const PLACES = 8;
const CENT = 10n ** BigInt(PLACES - 2);
const EVERY_MS = 4_000;

const dollars = (raw: bigint) => fmt(fromRaw(raw, PLACES), { prefix: "$", places: 2, compact: 5 });

/** The table's wins, newest first; its latest calls when nobody has won lately. */
function useHeadlines(): Trade[] | undefined {
  const trades = useRecentTrades().data;
  return useMemo(() => {
    if (!trades) return undefined;
    const sorted = [...trades].sort((a, b) => b.tsMs - a.tsMs);
    const wins = sorted.filter((trade) => trade.kind === "close" && (toRaw(trade.pnlUsd ?? "0", PLACES) ?? 0n) >= CENT);
    return (wins.length ? wins : sorted).slice(0, 8);
  }, [trades]);
}

function Headline({ trade, you }: { trade: Trade; you: boolean }) {
  const won = toRaw(trade.pnlUsd ?? "0", PLACES) ?? 0n;
  const notional = toRaw(trade.notionalUsd, PLACES) ?? 0n;
  const stake = trade.leverage > 0 ? notional / BigInt(trade.leverage) : notional;
  const who = you ? "You" : shortAddress(trade.wallet);
  const arrow = <span className={trade.isLong ? "up" : "down"}>{trade.isLong ? "▲" : "▼"}</span>;
  return (
    <span className="pu-line">
      <Avatar address={trade.wallet} size={20} />
      <span className={you ? "pu-you" : "pu-who mono"}>{who}</span>
      {trade.kind === "close" && won >= CENT ? (
        <>
          won <span className="pu-fig num up">+{dollars(won)}</span>
        </>
      ) : trade.kind === "liquidation" ? (
        "busted"
      ) : (
        <>
          called {arrow} <span className="pu-fig num">{dollars(stake)}</span>
        </>
      )}
      <span className="pu-on">
        {trade.kind !== "open" && arrow} {trade.instrument} {trade.leverage}×
      </span>
    </span>
  );
}

/** The table in a line: who just won, one at a time. */
export function Pulse() {
  const headlines = useHeadlines();
  const owner = useOwner();
  const [at, setAt] = useState(0);
  const count = headlines?.length ?? 0;

  useEffect(() => {
    if (count < 2) return;
    const timer = setInterval(() => setAt((index) => index + 1), EVERY_MS);
    return () => clearInterval(timer);
  }, [count]);

  const trade = count ? headlines?.[at % count] : undefined;
  return (
    <Link href="/floor" className="pu" aria-label="The table, live">
      <span className="pu-live" aria-hidden />
      {trade ? (
        <span key={trade.seq} className="pu-slide">
          <Headline trade={trade} you={owner !== null && trade.wallet.toLowerCase() === owner} />
        </span>
      ) : (
        <span className="pu-quiet">{headlines === undefined ? "Joining the table" : "Quiet for now"}</span>
      )}
    </Link>
  );
}
